import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { DivisionChange } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { CollectionArrow } from '@sorare/core/src/atoms/icons/CollectionArrow';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  LabelL,
  LabelM,
  LabelS,
  LabelXS,
} from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { glossary, playerDetails } from '@sorare/core/src/lib/glossary';

import { DumbStatus } from 'components/managerTeam/Status/DumbStatus';
import { colorPerBonusRating } from 'lib/bonus';

import { RankRewardsRanking_so5Ranking } from './__generated__/index.graphql';

const Root = styled.div`
  position: relative;
  display: flex;
  gap: var(--triple-unit);
  justify-content: space-between;
  align-items: start;
  border-radius: var(--unit);
  border: 1px solid var(--c-nd-150-opaque);
  background-color: var(--c-nd-50);
  padding: var(--unit) var(--intermediate-unit) var(--intermediate-unit);

  &.rank-1 {
    border: 1px solid #ae5e13;
  }

  &.rank-2 {
    border: 1px solid #bcc8cb;
  }

  &.rank-3 {
    border: 1px solid #cc8645;
  }

  &.isMyRanking {
    background-color: var(--c-nd-150);
    border: 1px solid var(--c-white);
  }
`;

const StyledButtonBase = styled(ButtonBase)`
  width: 100%;
  text-align: left;
`;

const UserInfo = styled(Horizontal)`
  margin-top: var(--half-unit);
`;

const AvatarContainer = styled(Horizontal).attrs({ gap: 0, center: true })`
  position: relative;
  border-radius: var(--quadruple-unit);
  background-color: var(--c-nd-400);

  & > img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Infos = styled(Vertical)`
  overflow: hidden;
`;

const RankingContainer = styled(LabelS)`
  position: absolute;
  right: 0;
  bottom: 0;
  padding: 2px 6px 2px var(--half-unit);
  border-radius: var(--unit) 0px var(--half-unit) 0px;
  background-color: var(--c-nd-100);
`;

const Scores = styled(Vertical)`
  align-items: flex-end;
`;

const StatusContainer = styled.div`
  position: absolute;
  display: flex;
  left: 0;
  top: 0;
  transform: translate(-3px, -3px);
`;

type Props = {
  so5Ranking: RankRewardsRanking_so5Ranking;
  isMyRanking?: boolean;
  onClick?: () => void;
};

export const Ranking = ({ so5Ranking, isMyRanking, onClick }: Props) => {
  const { user } = so5Ranking.so5Lineup;

  const bonusColor = so5Ranking.so5Lineup.bonusRating
    ? colorPerBonusRating[so5Ranking.so5Lineup.bonusRating]
    : 'var(--c-nd-600)';

  return (
    <Root
      key={so5Ranking.id}
      as={onClick ? StyledButtonBase : undefined}
      onClick={onClick}
      className={classNames(
        so5Ranking.eligibleDivisionChange,
        `rank-${so5Ranking.ranking}`,
        {
          isMyRanking,
        }
      )}
    >
      <UserInfo gap={1.5}>
        <AvatarContainer>
          {so5Ranking.eligibleDivisionChange &&
            so5Ranking.eligibleDivisionChange !== DivisionChange.STABLE && (
              <StatusContainer>
                <DumbStatus
                  divisionChange={so5Ranking.eligibleDivisionChange}
                  size={2}
                />
              </StatusContainer>
            )}
          <Avatar user={user} rounded size={6} />
        </AvatarContainer>
        <Infos gap={0.5}>
          <LabelL bold>
            <Ellipsis>{user.nickname}</Ellipsis>
          </LabelL>
          <LabelM color="var(--c-nd-700)">
            <Ellipsis>
              {so5Ranking.so5Lineup.managerTeam?.name ||
                so5Ranking.so5Lineup.name}
            </Ellipsis>
          </LabelM>
        </Infos>
      </UserInfo>
      <Scores gap={0.5}>
        <Horizontal gap={0.5} as="span">
          <LabelL as="span" bold>
            <FormattedNumber value={so5Ranking.score} />
          </LabelL>
          <LabelXS uppercase color="var(--c-nd-600)" as="span" bold>
            <FormattedMessage {...playerDetails.points} />
          </LabelXS>
        </Horizontal>

        <Horizontal gap={0.5} as="span">
          <LabelS bold color={bonusColor}>
            <Horizontal as="span" gap={0.5}>
              <CollectionArrow size={1} />
              +
              <FormattedNumber value={so5Ranking.so5Lineup.bonusPoints || 0} />
            </Horizontal>
          </LabelS>
          <LabelXS uppercase as="span" bold color={bonusColor}>
            <FormattedMessage {...playerDetails.points} />
          </LabelXS>
        </Horizontal>
        <RankingContainer as="p" bold>
          <FormattedMessage
            {...glossary.ordinal}
            values={{ ordinal: so5Ranking.ranking ?? 0 }}
          />
        </RankingContainer>
      </Scores>
    </Root>
  );
};

Ranking.fragments = {
  so5Ranking: gql`
    fragment RankRewardsRanking_so5Ranking on So5Ranking {
      id
      eligibleDivisionChange
      ranking
      score
      so5Lineup {
        id
        name
        bonusPoints
        bonusRating
        managerTeam {
          id
          name
        }
        user {
          slug
          nickname
          ...Avatar_publicUserInfoInterface
        }
      }
    }
    ${Avatar.fragments.publicUserInfoInterface}
  ` as TypedDocumentNode<RankRewardsRanking_so5Ranking>,
};
